import { Linking, Platform } from 'react-native'

/**
 * Apple Maps base URL
 * @constant {string}
 */
export const appleMapsUrl = 'https://maps.apple.com/'

/**
 * Google Maps universal, cross-platform URL for map direction
 * @constant {string}
 */
export const googleMapsUrl = 'https://www.google.com/maps/dir/'

/**
 * Google Maps iOS URL scheme
 * @constant {string}
 */
export const googleMapsIosScheme = 'comgooglemaps://'

/**
 * Launches map directions to a specific destination
 * - **iOS**: Tries Google Maps first (if installed), falls back to Apple Maps
 * - **Android**: Opens Google Maps universal, cross-platform URL
 * - **Web**: Opens Google Maps universal, cross-platform URL
 * @param {string} destinationName - The name of the destination (e.g., "Montreal General Hospital")
 * @param {string} destinationAddress - The address of the destination (e.g., "1650 Cedar Ave, Montreal, Quebec H3H 1V6")
 * @returns {Promise<void>} A promise that resolves when the map application is opened
 */
export const launchMapDirection = async (
  destinationName: string,
  destinationAddress: string
): Promise<void> => {
  const encodedDestinationName = encodeURIComponent(destinationName)
  const encodedDestinationAddress = encodeURIComponent(destinationAddress)

  switch (Platform.OS) {
    case 'ios':
      const canOpenGoogleMaps = await Linking.canOpenURL(
        `${googleMapsIosScheme}?daddr=${encodedDestinationName},${encodedDestinationAddress}`
      )

      if (canOpenGoogleMaps) {
        await Linking.openURL(
          `${googleMapsIosScheme}?daddr=${encodedDestinationName},${encodedDestinationAddress}`
        )
        break
      }

      await Linking.openURL(
        `${appleMapsUrl}?daddr=${encodedDestinationName},${encodedDestinationAddress}`
      )
      break
    case 'android':
    case 'web':
    default:
      await Linking.openURL(
        `${googleMapsUrl}?api=1&destination=${encodedDestinationName},${encodedDestinationAddress}`
      )
      break
  }
}
